/**
 * Lodestone Elevation utilities
 *
 * Elevation values come from the server (POST /api/routes/elevation-profile, backed by the
 * SRTM tiles in services/elevation); this module only derives distances and totals from them
 * and keeps the chart-to-map hover marker in sync.
 */

import { haversineDistance } from './utils';
import type { LngLat, HoverData } from './types';
import type { MapManager } from './maps';
import { createHoverMarkerElement } from './maps';
import type { Map as MapboxMap, Marker } from 'mapbox-gl';

/**
 * Calculate cumulative distances for an array of coordinates
 */
export function calculateCumulativeDistances(coords: LngLat[]): number[] {
    if (!coords || coords.length === 0) return [];

    const distances: number[] = [0];
    for (let i = 1; i < coords.length; i++) {
        const dist = haversineDistance(
            coords[i - 1][1], coords[i - 1][0],
            coords[i][1], coords[i][0]
        );
        distances.push(distances[i - 1] + dist);
    }
    return distances;
}

/**
 * Calculate total elevation gain from an array of elevation values
 * Only counts positive elevation changes (climbs)
 */
export function calculateElevationGain(elevations: number[]): number {
    if (!elevations || elevations.length < 2) return 0;

    let gain = 0;
    for (let i = 1; i < elevations.length; i++) {
        const diff = elevations[i] - elevations[i - 1];
        if (diff > 0) {
            gain += diff;
        }
    }
    return gain;
}

interface ElevationChartMapSyncOptions {
    markerColor?: string;
}

/**
 * Synchronizes elevation chart hover with a map marker
 * Provides a reusable abstraction for connecting elevation chart hover events
 * to a marker on the map polyline.
 *
 * Supports both MapManager instances and raw mapboxgl.Map objects.
 */
export class ElevationChartMapSync {
    private mapManager: MapManager | null;
    private map: MapboxMap | null;
    private markerColor: string;
    private marker: Marker | null = null;
    private markerAdded = false;

    constructor(mapOrManager: MapManager | MapboxMap, options: ElevationChartMapSyncOptions = {}) {
        // Detect if this is a MapManager (has addHoverMarker) or raw map
        if ('addHoverMarker' in mapOrManager && typeof mapOrManager.addHoverMarker === 'function') {
            this.mapManager = mapOrManager as MapManager;
            this.map = null;
        } else {
            this.mapManager = null;
            this.map = mapOrManager as MapboxMap;
        }
        this.markerColor = options.markerColor || '#10b981';
    }

    /**
     * Create the hover marker element
     */
    private createMarkerElement(): HTMLDivElement {
        return createHoverMarkerElement(this.markerColor);
    }

    /**
     * Returns the onHover callback to pass to ElevationChart
     * The callback handles showing/hiding the marker based on hover state
     */
    getOnHover(): (hoverData: HoverData | null) => void {
        return (hoverData: HoverData | null) => {
            if (this.mapManager) {
                // Use MapManager methods
                if (!this.markerAdded) {
                    this.mapManager.addHoverMarker({ color: this.markerColor });
                    this.markerAdded = true;
                }

                if (hoverData && hoverData.lngLat) {
                    this.mapManager.showHoverMarker(hoverData.lngLat);
                } else {
                    this.mapManager.hideHoverMarker();
                }
            } else if (this.map) {
                // Use raw map with our own marker
                if (!this.marker) {
                    const el = this.createMarkerElement();
                    this.marker = new mapboxgl.Marker({ element: el })
                        .setLngLat([0, 0])
                        .addTo(this.map);
                    el.style.display = 'none';
                }

                if (hoverData && hoverData.lngLat) {
                    this.marker.setLngLat(hoverData.lngLat);
                    this.marker.getElement().style.display = 'block';
                } else {
                    this.marker.getElement().style.display = 'none';
                }
            }
        };
    }

    /**
     * Clean up marker when done
     * Call this when the component/page is being destroyed
     */
    destroy(): void {
        if (this.mapManager && this.markerAdded) {
            this.mapManager.hideHoverMarker();
        }
        if (this.marker) {
            this.marker.remove();
            this.marker = null;
        }
    }
}
